import { logFlexDatesClick, logFlexDatesDisplay } from '@mfs/client/common/log-ga-event/logGaEvent-flex-dates'
import { cn } from '@pcln/horizon'
import { FlexDates } from '@pcln/horizon-fly-components'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { parseISO } from 'date-fns/parseISO'
import { startOfDay } from 'date-fns/startOfDay'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { useIsMobile } from '../../helpers/useIsMobile'
import { generateFlexDatesQuery } from '../../services/flexDates'
import { getFlexDates } from '../../services/flexDates/flexDatesApi'
import { getIsTravelDateCheapest, logPriceAccuracy, logResultsToSplunk } from './helpers'

export const FlexDatesWrapper = React.memo(function FlexDatesWrapper() {
  const [flexDatesData, setFlexDatesData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isSuperSearchLoading, setIsSuperSearchLoading] = useState(false)
  const [hasSuperSearchLoaded, setHasSuperSearchLoaded] = useState(false)

  const searchRequest = useSelector((state) => state.pclnBootstrapData.searchRequest)
  const minimumRetailPrice = useSelector((state) => state.listingsMetaData.minimumRetailPrice)
  const store = useStore()
  const [minimumListingPrice] = useState(() => store.getState().listingsMetaData.minimumListingPrice)
  const rawListings = useSelector((state) => state.listings?.response)
  const listings = useMemo(() => rawListings ?? [], [rawListings])
  const rawRecommendedListings = useSelector((state) => state.recommendedListings?.[1])
  const recommendedListings = useMemo(() => rawRecommendedListings ?? [], [rawRecommendedListings])
  const isMobile = useIsMobile()
  const activeTabId = useSelector((state) => state.activeTabId)
  const isNotReshopAndWLAndPackages = useSelector(
    (state) => state?.pclnBootstrapData?.isNotReshopAndWLAndPackages
  )
  const authUser = useSelector((state) => state.pclnBootstrapData?.authUser)
  const isUserLoggedIn = Boolean(authUser)
  const tripType = searchRequest?.params?.tripType
  const travelDate = searchRequest?.slices?.[0]?.travelDate
  const sliceInfo = searchRequest.slices[0]

  const isVisible =
    (tripType === 'OW' || tripType === 'RT') && activeTabId === 1 && isNotReshopAndWLAndPackages

  const travelDateHasExd = minimumListingPrice !== null && minimumListingPrice < minimumRetailPrice

  useEffect(() => {
    if (!isLoading) {
      logPriceAccuracy({
        sliceInfo,
        recommendedListings,
        listings,
        minimumRetailPrice,
        minimumListingPrice,
        error,
        tripType,
      })
    }
  }, [error, isLoading, listings, minimumListingPrice, minimumRetailPrice, recommendedListings, sliceInfo, tripType])

  const getFlexDatesRequest = (isSuperSearch = false) => {
    // Calculate the difference between travel date and today
    const rangePlus = 3
    const parsedTravelDate = parseISO(travelDate)
    const today = startOfDay(new Date())
    const rangeMinus =
      differenceInCalendarDays(parsedTravelDate, today) >= rangePlus
        ? rangePlus
        : differenceInCalendarDays(parsedTravelDate, today)

    const { query } = generateFlexDatesQuery({
      requestObj: searchRequest,
      tripType,
      rangePlusMinus: 0,
      rangePlus,
      rangeMinus: rangeMinus,
      requestId: uuidv4(),
      fareType: 'ALL',
    })

    if (isSuperSearch) {
      query.isSuperSearch = true
      query.minimumPrice = minimumListingPrice
    }

    return query
  }

  useEffect(() => {
    if (!isVisible) return

    const fetchFlexDates = async () => {
      try {
        const request = getFlexDatesRequest()
        const data = await getFlexDates({
          request,
          tripType,
        })

        const validDates = data.filter((date) => date.dates.length > 0)
        setFlexDatesData(validDates)

        if (data.length > 0) {
          logFlexDatesDisplay({ flexDates: data, minimumListingPrice, travelDateHasExd })
        }

        logResultsToSplunk({
          data,
          request,
          tripType,
          minimumRetailPrice,
          searchRequest,
          isTravelDateCheapest: getIsTravelDateCheapest({
            flexDates: data,
            travelDate,
            minimumRetailPrice,
          }),
        })
      } catch (err) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchFlexDates()
  }, [])

  const handleSuperSearchClick = async () => {
    setIsSuperSearchLoading(true)
    const request = getFlexDatesRequest(true)
    let data = []
    try {
      data = await getFlexDates({ request, tripType })
      setFlexDatesData((prev) => [...prev, ...data])
      setHasSuperSearchLoaded(true)
    } catch {
      // fail silently — base flex dates remain visible
    } finally {
      setIsSuperSearchLoading(false)
    }
    logResultsToSplunk({
      data,
      request,
      tripType,
      minimumRetailPrice,
      searchRequest,
      isSuperSearch: true,
    })
  }

  if (error || !isVisible) {
    return null
  }

  const showSuperSearchChip = !isLoading && !hasSuperSearchLoaded && flexDatesData.length > 0

  return (
    <div className={cn('flex mb-2', isUserLoggedIn ? 'mt-4 lg:mt-0' : '')}>
      <div className='h-fit px-2 lg:px-0 w-full'>
        <FlexDates
          flexDatesData={flexDatesData}
          isLoading={isLoading}
          logFlexDatesClick={(index) =>
            logFlexDatesClick({
              flexDates: flexDatesData,
              minimumListingPrice,
              selectedIndex: index,
              travelDate,
              sliceInfo,
            })
          }
          tripType={tripType}
          travelDate={travelDate}
          minimumRetailPrice={minimumListingPrice}
          showSuperSearchChip={showSuperSearchChip}
          handleSuperSearchClick={handleSuperSearchClick}
          isSuperSearchLoading={isSuperSearchLoading}
          isMobile={isMobile}
        />
      </div>
    </div>
  )
})
